import React from "react";
import styles from "./BetTableCmatch20.module.css";

const ScoreBox = ({ bet, index, getExposure, getBackExposure, getLayExposure }) => {
  // Get exposure for back and lay separately
  const sid = bet.betData?.sid;
  const backExposure = getBackExposure ? getBackExposure(bet.type, bet.betData?.nat, sid) : getExposure(bet.type, bet.betData?.nat, sid);
  const layExposure = getLayExposure ? getLayExposure(bet.type, bet.betData?.nat, sid) : getExposure(bet.type, bet.betData?.nat, sid);

  // Extract run number from "Run 2", "Run 3", etc.
  const runNumber = bet.type?.replace(/[^0-9]/g, '') || (index + 2);
  const ballImageSrc = `/assets/img/ct20/balls/ball${runNumber}.png`;
  const batImageSrc = `/assets/img/ct20/score-bat-icon.png`;

  // Format exposure value
  const formatExposure = (value) => {
    if (!value || value === 0) return "0";
    return value < 0 ? value.toFixed(0) : `+${value.toFixed(0)}`;
  };

  // Format min/max values
  const formatLimit = (value) => {
    if (!value && value !== 0) return "100";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "100";
    if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(0)}K`;
    }
    return numValue.toString();
  };

  return (
    <div className={`${styles.scoreBox} ${styles.btnTheme} ${bet.isLocked ? styles.suspended : ''}`}>
      <img 
        src={ballImageSrc} 
        className={`${styles.imgFluid} ${styles.ballImage}`}
        alt={`Ball ${runNumber}`}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      <img 
        src={batImageSrc} 
        className={styles.scoreImg}
        alt="Score bat"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      
      <div className={styles.book} style={{ color: 'white' }}>
        {formatExposure(backExposure || layExposure)} 
      </div>

      <div className={styles.minMax}>
        <span>Min:<span>{formatLimit(bet.min)}</span> Max:<span>{formatLimit(bet.max)}</span></span>
      </div>

      <div className={`${styles.back} ${styles.backbox}`}>
        <span className={`${styles.odds} ${styles.dBlock}`}>
          {bet.isLocked ? "0" : (bet.b || 0)}
        </span>
      </div>

      <div className={`${styles.lay} ${styles.laybox}`}>
        <span className={`${styles.odds} ${styles.dBlock}`}>
          {bet.isLocked ? "0" : (bet.l || 0)}
        </span>
      </div>
    </div>
  );
};

export default function BetTableCmatch20({ data, exposures = {}, gameData = {} }) {
  // Data structure: Run 2, Run 3, Run 4, Run 5, Run 6, Run 7, Run 8, Run 9, Run 10
  // Map all items directly from the data array
  const bets = (data || []).map((item, index) => ({
    type: item?.nat || `Run ${index + 2}`,
    b: item?.b || 0,  // Back odds
    l: item?.l || 0,  // Lay odds
    odds: item?.b,
    layOdds: item?.l,
    limit: item?.bs,
    min: item?.min,
    max: item?.max,
    isLocked: isLocked(item?.b, item?.gstatus),
    betData: item // Include full bet data
  }));

  function isLocked(val, gstatus) {
    return val === 0 || val === undefined || val === null || gstatus?.toUpperCase() === "SUSPENDED";
  }

  // Get exposure value for a bet type (general)
  const getExposure = (betType, nat, sid) => {
    if (!exposures || Object.keys(exposures).length === 0) {
      return 0;
    }
    
    // Try multiple key variations to match exposure
    const exposureKeys = [
      nat,                                    // "Run 2"
      betType,                                // "Run 2"
      nat?.toLowerCase(),                     // "run 2"
      betType?.toLowerCase(),                 // "run 2"
      nat?.trim(),                            // "Run 2" (trimmed)
      betType?.trim(),                        // "Run 2" (trimmed)
      nat?.replace(/\s+/g, ' ').trim(),      // "Run 2" (normalized)
      betType?.replace(/\s+/g, ' ').trim(),  // "Run 2" (normalized)
      sid?.toString(),                        // "1", "2", etc. (sid as string)
      `sid_${sid}`,                           // "sid_1", "sid_2", etc.
      `${sid}`,                               // "1", "2" (as number key)
    ];
    
    for (const key of exposureKeys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue) && expValue !== 0) {
          return expValue;
        }
      }
    }
    
    // Try matching against all exposure keys with normalization
    const normalizedBetType = betType?.toLowerCase().trim();
    const normalizedNat = nat?.toLowerCase().trim();
    
    for (const expKey in exposures) {
      const normalizedExpKey = expKey?.toLowerCase().trim();
      if (normalizedExpKey === normalizedBetType || normalizedExpKey === normalizedNat) {
        const expValue = parseFloat(exposures[expKey]);
        if (!isNaN(expValue) && expValue !== 0) {
          return expValue;
        }
      }
    }
    
    // Try partial matching (e.g., "run2" matches "Run 2")
    const cleanBetType = betType?.replace(/\s+/g, '').toLowerCase();
    const cleanNat = nat?.replace(/\s+/g, '').toLowerCase();
    
    for (const expKey in exposures) {
      const cleanExpKey = expKey?.replace(/\s+/g, '').toLowerCase();
      if (cleanExpKey === cleanBetType || cleanExpKey === cleanNat) {
        const expValue = parseFloat(exposures[expKey]);
        if (!isNaN(expValue) && expValue !== 0) {
          return expValue;
        }
      }
    }
    
    return 0;
  };

  // Get exposure for back bets
  const getBackExposure = (betType, nat, sid) => {
    // Try with "back" suffix
    const backKeys = [
      `${nat} back`,
      `${betType} back`,
      `${nat?.toLowerCase()} back`,
      `${betType?.toLowerCase()} back`,
    ];
    
    for (const key of backKeys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue)) {
          return expValue;
        }
      }
    }
    
    // Fallback to general exposure
    return getExposure(betType, nat, sid);
  };

  // Get exposure for lay bets
  const getLayExposure = (betType, nat, sid) => {
    // Try with "lay" suffix
    const layKeys = [
      `${nat} lay`,
      `${betType} lay`,
      `${nat?.toLowerCase()} lay`,
      `${betType?.toLowerCase()} lay`,
    ];
    
    for (const key of layKeys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue)) {
          return expValue;
        }
      }
    }
    
    // Fallback to general exposure
    return getExposure(betType, nat, sid);
  };

  // Split bets into two columns (first 5 in left, next 5 in right)
  const leftColumnBets = bets.slice(0, 5);
  const rightColumnBets = bets.slice(5, 10);

  return (
    <div className={styles.cmatchTable20}>
      <div className="row row5">
        <div className="col-6">
          {leftColumnBets.map((bet, index) => (
            <ScoreBox
              key={index}
              bet={bet}
              index={index}
              getExposure={getExposure}
              getBackExposure={getBackExposure}
              getLayExposure={getLayExposure}
            />
          ))}
        </div>
        <div className="col-6">
          {rightColumnBets.map((bet, index) => (
            <ScoreBox
              key={index + 5}
              bet={bet}
              index={index + 5}
              getExposure={getExposure}
              getBackExposure={getBackExposure}
              getLayExposure={getLayExposure}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

